import { useState, useEffect, lazy, Suspense } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { ImageUpload } from './ImageUpload';
import { SimilarPostsSidebar } from './SimilarPostsSidebar';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';

const MarkdownEditor = lazy(() =>
  import('@/components/MarkdownEditor').then((mod) => ({ default: mod.MarkdownEditor }))
);
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertCircle } from 'lucide-react';

interface CreatePostModalProps {
  boardId: string;
  boardName: string;
  isOpen: boolean;
  onClose: () => void;
  categories?: Array<{
    id: string;
    name: string;
    color?: string;
  }>;
}

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

export function CreatePostModal({
  boardId,
  boardName,
  isOpen,
  onClose,
  categories = [],
}: CreatePostModalProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [imageURLs, setImageURLs] = useState<string[]>([]);
  const [allowContact, setAllowContact] = useState(false);
  const [error, setError] = useState('');

  const debouncedTitle = useDebounce(title, 300);

  const createPostMutation = useMutation({
    mutationFn: (data: { title: string; details: string; boardId: string; categoryId?: string; imageURLs?: string[]; allowContact: boolean }) =>
      api.posts.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['posts', boardId] });
      queryClient.invalidateQueries({ queryKey: ['boards'] });
      onClose();
      setTitle('');
      setDetails('');
      setCategoryId('');
      setImageURLs([]);
      setAllowContact(false);
      setError('');
      toast.success('Post created');
      // Navigate to the new post
      navigate(`/posts/${response.post.slug}`);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to create post');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    createPostMutation.mutate({
      title: title.trim(),
      details: details.trim(),
      boardId,
      ...(categoryId && { categoryId }),
      ...(imageURLs.length > 0 && { imageURLs }),
      allowContact,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden p-0">
        <div className="flex flex-col md:flex-row h-full max-h-[90vh]">
          {/* Main form area */}
          <div className="flex-1 p-6 overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl">Create a post</DialogTitle>
              <DialogDescription>in {boardName}</DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="title">
                  Title <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Brief summary of your feedback"
                  maxLength={200}
                  required
                />
                <p className="text-xs text-muted-foreground">{title.length}/200 characters</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="details">Details (optional)</Label>
                <Suspense fallback={<div className="h-32 w-full bg-muted animate-pulse rounded-md" />}>
                  <MarkdownEditor
                    content={details}
                    onChange={setDetails}
                    placeholder="Provide more context, use cases, or examples..."
                  />
                </Suspense>
              </div>

              {categories.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="category">Category (optional)</Label>
                  <Select value={categoryId} onValueChange={setCategoryId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem key={category.id} value={category.id}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>Images (optional)</Label>
                <ImageUpload
                  onImagesChange={setImageURLs}
                  maxImages={3}
                  type="posts"
                />
              </div>

              <div className="flex items-start gap-3 rounded-lg border border-border p-4">
                <Checkbox
                  id="allowContact"
                  checked={allowContact}
                  onCheckedChange={(checked) => setAllowContact(checked === true)}
                  className="mt-0.5"
                />
                <div className="space-y-1">
                  <Label htmlFor="allowContact" className="text-sm font-medium cursor-pointer">
                    I'm open to discussing this idea with the product team
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Sometimes we need a bit more context to get the details right. Give our team permission to reach out for a brief interview or clarification to help us build this better for you.
                  </p>
                </div>
              </div>

              <DialogFooter className="pt-4">
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createPostMutation.isPending || !title.trim()}
                >
                  {createPostMutation.isPending ? 'Creating...' : 'Create post'}
                </Button>
              </DialogFooter>
            </form>
          </div>

          {/* Sidebar - hidden on mobile */}
          <div className="hidden md:block w-72 border-l border-border bg-muted/30 p-4 overflow-y-auto">
            <SimilarPostsSidebar boardId={boardId} title={debouncedTitle} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
